import React, { useState } from 'react';
import { Form, Formik } from 'formik';
import Button from '@/components/shared/Button';
import MultiStepSignupStep1 from './MultiStepSignupStep1';
import MultiStepSignupStep2 from './MultiStepSignupStep2';
import MultiStepSignupStep3 from './MultiStepSignupStep3';
import MultiStepSignupStep4 from './MultiStepSignupStep4';
import {
  IMultiStepSignupValues,
  getDefaultValues,
  getTotalSteps,
  getStepFields,
  getValidationSchemaForStep
} from './types';
import { UserRole } from '@/types';
import logo from '@/img/propsavvy-logo-with-text.svg';
import './MultiStepSignup.scss';

const baseClass = 'MultiStepSignup';

interface Props {
  userRole?: UserRole;
  initialValues?: Partial<IMultiStepSignupValues>;
  onSubmit: (values: IMultiStepSignupValues) => Promise<void>; // Изменено на Promise<void>
  isSubmitting?: boolean;
}

const MultiStepSignup: React.FC<Props> = ({
                                            userRole = UserRole.OWNER,
                                            initialValues = {},
                                            onSubmit,
                                            isSubmitting = false,
                                          }) => {
  const [currentStep, setCurrentStep] = useState(1);

  const formInitialValues = {
    ...getDefaultValues(userRole),
    ...initialValues
  };

  // Track dynamic role changes based on form values
  const [dynamicRole, setDynamicRole] = useState<UserRole>(userRole);
  const formSteps = getTotalSteps(dynamicRole);
  const totalSteps = formSteps + 1; // Add confirmation step

  const handleNext = async (validateForm: () => Promise<any>) => {
    const errors = await validateForm();
    const stepFields = getStepFields(currentStep, dynamicRole);
    const hasStepErrors = stepFields.some(field => errors[field]);

    if (!hasStepErrors) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const handlePrev = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleFormSubmit = async (values: IMultiStepSignupValues) => { // Сделано асинхронным
    try {
      await onSubmit(values); // Ожидаем завершения onSubmit
      setCurrentStep(totalSteps); // Переходим на шаг подтверждения только в случае успеха
    } catch (error) {
      // Ошибка уже обработана в onSubmit (handleMultiStepSubmit в SignupPage.tsx)
      // Остаемся на текущем шаге, чтобы пользователь мог увидеть ошибку.
      console.error("Form submission failed:", error);
    }
  };

  const renderStep = (values: IMultiStepSignupValues, formProps: any) => {
    const { errors, touched, handleChange, handleBlur, setFieldValue, setFieldError } = formProps;
    const currentRole = values.role;
    const stepProps = {
      userRole: currentRole,
      values,
      errors,
      touched,
      handleChange,
      handleBlur,
      setFieldValue,
      setFieldError, // Передаем setFieldError
    };

    if (currentStep === totalSteps) {
      return <MultiStepSignupStep4 email={values.email} />;
    }

    if (currentRole === UserRole.OWNER) {
      switch (currentStep) {
        case 1:
          return <MultiStepSignupStep1 {...stepProps} />;
        case 2:
          return <MultiStepSignupStep3 {...stepProps} />;
        default:
          return null;
      }
    } else {
      switch (currentStep) {
        case 1:
          return <MultiStepSignupStep1 {...stepProps} />;
        case 2:
          return <MultiStepSignupStep2 {...stepProps} />;
        case 3:
          return <MultiStepSignupStep3 {...stepProps} />;
        default:
          return null;
      }
    }
  };

  return (
    <div className={baseClass}>
      <div className={`${baseClass}__view`}>
        <div className={`${baseClass}__logo`}>
          <img src={logo} alt="PropSavvy logo" />
          <p className={`${baseClass}__logo-desc`}>
            {currentStep === totalSteps ? '' : 'Please enter your user information.'}
          </p>
        </div>

        {/* Progress Indicator */}
        {currentStep <= formSteps && (
          <div className={`${baseClass}__progress`}>
            <div className={`${baseClass}__progress-bar`}>
              <div
                className={`${baseClass}__progress-fill`}
                style={{ width: `${(currentStep / formSteps) * 100}%` }}
              />
            </div>
            <span className={`${baseClass}__progress-text`}>
              Step {currentStep} of {formSteps}
            </span>
          </div>
        )}

        <Formik
          enableReinitialize={true}
          initialValues={formInitialValues}
          validationSchema={getValidationSchemaForStep(currentStep, dynamicRole)}
          onSubmit={handleFormSubmit}
        >
          {(formProps) => {
            const { values, validateForm } = formProps;

            if (values.role !== dynamicRole) {
              setDynamicRole(values.role);
            }

            return (
              <>
                <Form className={`${baseClass}__form`}>
                  <div className={`${baseClass}__form-content`}>
                    {renderStep(values, formProps)}
                  </div>

                  <div className={`${baseClass}__form-actions`}>
                    {currentStep > 1 && currentStep <= formSteps && (
                      <Button
                        type="button"
                        onClick={handlePrev}
                        className={`${baseClass}__btn-secondary`}
                      >
                        Back
                      </Button>
                    )}

                    {currentStep < formSteps ? (
                      <Button
                        type="button"
                        onClick={() => handleNext(validateForm)}
                        className={`${baseClass}__btn-primary`}
                      >
                        Next
                      </Button>
                    ) : currentStep === formSteps ? (
                      <Button
                        type="submit"
                        isDisabled={isSubmitting || !values.agreeToTerms}
                        isLoading={isSubmitting}
                        className={`${baseClass}__btn-primary`}
                      >
                        {isSubmitting ? 'Creating account...' : 'Sign up'}
                      </Button>
                    ) : null}
                  </div>
                </Form>

                {currentStep <= formSteps && (
                  <div className={`${baseClass}__bottom-links`}>
                    <a href="/login" className={`${baseClass}__bottom-links-signin`}>
                      Already member? Sign in
                    </a>
                    <a href="/forgot" className={`${baseClass}__bottom-links-forgot`}>
                      Forgot password?
                    </a>
                  </div>
                )}
              </>
            );
          }}
        </Formik>
      </div>
    </div>
  );
};

export default MultiStepSignup;
